import _ from 'lodash'
import Mustache from 'mustache'

import DEFAULTS from 'core/data-model/constants/defaults'
import CONSTANTS from 'core/data-model/constants/messages'
import MESSAGES from 'core/data-model/constants/validation-messages'
import VALUES from 'core/data-model/constants/values'
import { renderUnit } from 'core/service/insured/age'
import { calculateAge, convertEntryAge, isValidAge, isValidBirthdate } from 'core/service/insured/birthdate'
import { createChecker } from 'core/service/lib/check'
import { isPartiallyValidDate } from 'core/service/lib/date'

export const isPartiallyFilled = (birthdate) => {
  return birthdate.length < 10
}

export const _validateBirthdate = (birthdate, entryAge, previousAge, currentDate, isPayerBirthdate = false) => {
  const error = []

  const isValidBirthdateInput = isPartiallyFilled(birthdate)
    ? isPartiallyValidDate(birthdate)
    : isValidBirthdate(birthdate, currentDate)

  const isReadyToCalculateAge = !isPartiallyFilled(birthdate) && isValidBirthdateInput

  if (isReadyToCalculateAge) {
    let age = calculateAge(birthdate, currentDate)
    if (!isValidAge(age.value, age.unit, entryAge)) {
      const owner = isPayerBirthdate ? CONSTANTS.PAYER : CONSTANTS.INSURED_PERSON
      error.push(
        Mustache.render(MESSAGES.RULE_AGE, {
          owner,
          minimumAge: `${entryAge.minimum.value} ${renderUnit(entryAge.minimum.unit)}`,
          maximumAge: `${entryAge.maximum.value} ${renderUnit(entryAge.maximum.unit)}`,
        })
      )
      age = { value: 0, unit: 'day' }
    }
    return { birthdate, error, age }
  } else {
    if (!isValidBirthdateInput || (previousAge.value === 0 && previousAge.unit === 'day')) {
      error.push(MESSAGES.RULE_BIRTHDATE)
    }
    return { birthdate, error, age: previousAge }
  }
}

export const _getEntryAge = (basicPlan, isPayerBirthdate) => {
  const config = basicPlan.product || basicPlan
  if (isPayerBirthdate) {
    const payerEntryAgeMinimum = _.get(config, 'payerEntryAgeMinimum', -1)
    if (payerEntryAgeMinimum !== -1) {
      return {
        minimum: convertEntryAge(payerEntryAgeMinimum),
        maximum: convertEntryAge(_.get(config, 'payerEntryAgeMaximum', DEFAULTS.DEFAULT_INSURED_ENTRY_AGE_MAXIMUM)),
      }
    } else {
      return DEFAULTS.DEFAULT_PAYER_ENTRY_AGE
    }
  }

  const insuredEntryAgeMinimum = _.get(config, 'insuredEntryAgeMinimum', DEFAULTS.DEFAULT_INSURED_ENTRY_AGE_MINIMUM)
  const insuredEntryAgeMaximum = _.get(config, 'insuredEntryAgeMaximum', DEFAULTS.DEFAULT_INSURED_ENTRY_AGE_MAXIMUM)
  return {
    minimum: convertEntryAge(insuredEntryAgeMinimum),
    maximum: convertEntryAge(insuredEntryAgeMaximum),
  }
}

export const _validateOccupationFactor = (basicPlanOccupationFactor) =>
  basicPlanOccupationFactor === -1 ? [MESSAGES.RULE_INSURED_OCCUPATION_FACTOR] : []

export const _validateProductOccupationFactor = (basicPlanOccupationFactor) =>
  basicPlanOccupationFactor === -1 ? [MESSAGES.RULE_INSURED_PRODUCT_OCCUPATION_FACTOR] : []

// ==== name validation ====

const charRange = (startChar, endChar) =>
  _.range(startChar.charCodeAt(0), endChar.charCodeAt(0) + 1).map((c) => String.fromCharCode(c))

const thaiChars = _.concat(charRange('ก', 'ฮ'), charRange('ฯ', 'ฺ'), charRange('เ', 'ํ'))

const englishChars = _.concat(charRange('a', 'z'), charRange('A', 'Z'))

export const _allowedChars = _.concat(thaiChars, englishChars, [' ', '.', '-'])

const validateName = (minLength, maxLength) => (name) => {
  const checker = createChecker([
    [
      `lengthLessThanOrEqual${maxLength}`,
      {
        type: 'VALIDATION',
        rule: (_, name) => name.length >= minLength && name.length <= maxLength,
        message: MESSAGES.RULE_NAME_EXCEED_LENGTH,
      },
    ],
    [
      'onlyThaiOrEnglishAndNotIncludeBothDotAndDash',
      {
        type: 'VALIDATION',
        rule: (_, name) => isPersonName(name),
        message: MESSAGES.RULE_NAME_INVALID_CHARACTER,
      },
    ],
  ])

  return checker({}, name).getErrorMessages('VALIDATION')
}

const _validateTitle = (minLength, maxLength) => (value) => {
  const checker = createChecker([
    [
      'notIncludeNumber',
      {
        type: 'VALIDATION',
        rule: (_, value) => value.value !== '' && isTitle(value.text),
        message: MESSAGES.RULE_TITLE_EMPTY_OTHER,
      },
    ],
  ])

  return checker({}, value).getErrorMessages('VALIDATION')
}

const validatePattern = (pattern) => (text) => pattern.test(text)
export const isPersonName = validatePattern(
  /^(?![.\- ])(?!.*[.]{2})(?!.*[-]{2})(?!.*[ ]{2})[a-zA-Z\u0E01-\u0E5B\s\.\-]*$/
)
export const isTitle = validatePattern(/[^0-9]/)
export const validateTitle = _validateTitle(VALUES.TITLE_MIN_LENGTH, VALUES.TITLE_MAX_LENGTH)
export const validateFirstName = validateName(VALUES.FIRST_NAME_MIN_LENGTH, VALUES.FIRST_NAME_MAX_LENGTH)
export const validateLastName = validateName(VALUES.LAST_NAME_MIN_LENGTH, VALUES.LAST_NAME_MAX_LENGTH)

/**
 * @param displayProductService {ReturnType<import('core/service/display-product/display-product').createDisplayProductService>}
 * @param insuredOccupationService {ReturnType<import('core/service/insured/occupation').createInsuredOccupationService>}
 */
export function createInsuredValidateService(displayProductService, insuredOccupationService) {
  const validateBirthdate = async (
    birthdate,
    displayProductQuery,
    previousAge,
    currentDate,
    isPayerBirthdate = false
  ) => {
    const basicPlan = await displayProductService.getBasicPlan(displayProductQuery)
    const entryAge = _getEntryAge(basicPlan, isPayerBirthdate)
    return _validateBirthdate(birthdate, entryAge, previousAge, currentDate, isPayerBirthdate)
  }

  const validateOccupationFactor = async (displayProductQuery, natureOfDutyCode) =>
    insuredOccupationService.getOccupationFactor(displayProductQuery, natureOfDutyCode).then(_validateOccupationFactor)

  //TODO this function can be grouped with validateOccupationFactor
  const validateValidOccupationFactor = async (displayProductQuery, natureOfDutyCode) =>
    insuredOccupationService
      .getOccupationFactorByValidOccupations(displayProductQuery, natureOfDutyCode)
      .then(_validateProductOccupationFactor)

  return {
    validateBirthdate,
    validateOccupationFactor,
    validateValidOccupationFactor,
  }
}
